import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { genericPointService } from '../services/GenericPointService';

const SORT_FIELD_MAP = {
    id: 'index',
    index: 'index',
    code: 'code',
    haystackTag: 'haystack_tag',
    brick: 'brick',
    description: 'description',
    units: 'units',
    dataType: 'data_type',
    defaultValue: 'default_value',
};

const GenericPointContext = createContext(null);

export function GenericPointProvider({ children }) {
    const [genericPoints, setGenericPoints] = useState([]);
    const [total, setTotal] = useState(0);
    const [pointType, setPointType] = useState('EP');
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sortState, setSortState] = useState([{ fieldName: 'id', dir: 'asc' }]);
    const [loading, setLoading] = useState(false);

    const getGenericPoints = useCallback(async () => {
        setLoading(true);
        try {
            const sortBy = SORT_FIELD_MAP[sortState[0]?.fieldName] ?? 'code';
            const orderBy = sortState[0]?.dir ?? 'asc';
            const result = await genericPointService.getGenericPoints(
                pageSize, pageIndex, sortBy, orderBy, pointType
            );
            if (result) {
                setGenericPoints(result.genericPoints);
                setTotal(result.total);
            }
        } finally {
            setLoading(false);
        }
    }, [pageSize, pageIndex, sortState, pointType]);

    const createGenericPoint = useCallback(async (request) => {
        return await genericPointService.createGenericPoint(request);
    }, []);

    const updateGenericPoint = useCallback(async (request) => {
        return await genericPointService.updateGenericPoint(request);
    }, []);

    const deleteGenericPoint = useCallback(async (id) => {
        return await genericPointService.deleteGenericPoint(id);
    }, []);

    const values = useMemo(() => ({
        genericPoints,
        total,
        pointType,
        pageIndex,
        pageSize,
        sortState,
        loading,
        setPointType,
        setPageIndex,
        setPageSize,
        setSortState,
        getGenericPoints,
        createGenericPoint,
        updateGenericPoint,
        deleteGenericPoint,
    }), [genericPoints, total, pointType, pageIndex, pageSize, sortState, loading, getGenericPoints, createGenericPoint, updateGenericPoint, deleteGenericPoint]);

    return (
        <GenericPointContext.Provider value={values}>
            {children}
        </GenericPointContext.Provider>
    );
}

export function useGenericPoints() {
    const ctx = useContext(GenericPointContext);
    if (!ctx) throw new Error('useGenericPoints must be used within GenericPointProvider');
    return ctx;
}
